import type { CartItemProduct, CartLineItem, Pricing } from '@/types/checkout';
import { formatPriceWithCurrency } from '@/utils/pricing';
import {
  getCartLineItemAssetUrl,
  getCartLineItemDisplayName,
  getCartLineItemQuantityLabel,
  shouldShowCartLineItemQuantityBadge,
} from '@/utils/offers';

const formatProductQuantity = (product: CartItemProduct): string => product.quantityDisplay ?? String(product.quantity);

export const OfferListItem = ({ lineItem, pricing }: { lineItem: CartLineItem; pricing: Pricing }) => {
  const products = lineItem.offer.products ?? [];
  const showQuantityBadge = shouldShowCartLineItemQuantityBadge(lineItem);

  return (
    <div
      data-testid="offers-list-item"
      className="relative flex w-full gap-2 border-b border-[#dedede] pb-[18px] last:border-b-0 last:pb-0"
    >
      <div className="size-10 shrink-0 overflow-hidden rounded-[4px] border border-current bg-white">
        <img
          src={getCartLineItemAssetUrl(lineItem)}
          alt={getCartLineItemDisplayName(lineItem)}
          className="size-full object-contain"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 flex-1 line-clamp-2 text-sm font-semibold leading-5 break-words">
            {getCartLineItemDisplayName(lineItem)}
          </p>
          <p className="shrink-0 text-sm font-semibold leading-4" data-testid="offer-line-item-price">
            {formatPriceWithCurrency(lineItem.price, pricing)}
          </p>
        </div>
        {products.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="offer-product-rows">
            {products.map((product) => (
              <div key={product.sku} className="flex items-center justify-between gap-2 text-xs opacity-70">
                <span className="min-w-0 truncate">{product.displayName ?? product.name}</span>
                <span className="shrink-0">{formatProductQuantity(product)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {showQuantityBadge && (
        <div className="pointer-events-none absolute left-[23px] top-[30px] flex h-3.5 min-w-[21px] items-center justify-center rounded-full bg-black backdrop-blur-sm">
          <span className="text-[8px] font-semibold leading-[11px] text-white" data-testid="offer-quantity-badge">
            {getCartLineItemQuantityLabel(lineItem)}
          </span>
        </div>
      )}
    </div>
  );
};
